import DarkSwitch from '@xfinity/dark-switch';

import localFlags from './local';
import fallbackFlags from './fallbackFlags';
import getConfig from '../../config';

import { getUserSegment } from '../segmentation';

const { env, featureFlagEndpoint } = getConfig();

const options = {
  allowQueryToggle: env !== 'prod',
};

if (env === 'local') {
  // Use local flags for local dev so we don't
  // need to make any CMS changes at the beginning.
  options.bootstrapFlags = localFlags;
} else {
  options.remoteUrl = featureFlagEndpoint;
  options.fallbackFlags = fallbackFlags;
}

const client = DarkSwitch.initialize(options);

const defaultCustomEnable = (flagValue) => {
  if (flagValue === true) return true;
  if (!flagValue) return false;
  // CMS JSON sends all values as string,
  // so we'll parse our own booleans.
  if (flagValue === 'true') return true;
  if (flagValue === 'false') return false;

  // Try to parse the flagValue into a number
  const numericFlagValue = parseInt(flagValue, 10);
  // and return early if it's NaN
  if (isNaN(numericFlagValue)) return false;

  // Fast finishes for flagValues outside of (0..100)
  if (numericFlagValue >= 100) return true;
  if (numericFlagValue <= 0) return false;

  // flagValue is a number between 0 and 100 exclusive beyond this point
  // and the user's segment should be the same.
  return numericFlagValue > getUserSegment();
};

// Start with just the roots we know the app should be aware of
const rootKeys = Object.keys(localFlags);
let flagsLoaded = false;

client.onReady(() => {
  // Add all roots from the JSON response.
  const allRoots = Array.from(new Set([...rootKeys, Object.keys(client.allFlags())]));
  flagsLoaded = true;
  rootKeys.splice(0, rootKeys.length, ...allRoots);
});

const mkNormalizePath = app => (path) => {
  if (path.indexOf('.') !== -1) {
    const first = path.split('.')[0];
    // If the first item is a known root, use it.
    if (first === 'global' || rootKeys.indexOf(first) !== -1) {
      return path;
    }
  }
  // autoprefix the path if not global because it's kinda annoying
  return `${app}.${path}`;
};

const normalizePath = mkNormalizePath('devices');

export const flagEnabled = (flagNameOrPath, { customEnable, ...rest } = {}) => client.flagEnabled(
  normalizePath(flagNameOrPath),
  {
    // Could also do a composition of the defaultBooleanCustomEnable and
    // default the customEnable param here to an identity function.
    customEnable: typeof customEnable === 'function'
      ? customEnable
      : defaultCustomEnable,
    ...rest,
  },
);

export const isReady = () => flagsLoaded;

export default client;
